import type { Options as HighchartsOptions, SeriesOptionsType } from 'highcharts';
import type {
    NestedOptions,
    Options as EditableOption,
} from '@highcharts/dashboards/es-modules/Dashboards/Components/EditableOptions.js';
import type { Options as HighchartsComponentOptions } from '@highcharts/dashboards/es-modules/Dashboards/Components/HighchartsComponent/HighchartsComponentOptions.js';
import { Dashboards } from '../dashboards-core';

/** The chart types an author may pick in the edit-mode sidebar. */
export const DASHBOARD_CHART_TYPES = [
    'line',
    'spline',
    'area',
    'areaspline',
    'column',
    'bar',
    'heatmap',
    'pie',
    'scatter',
] as const satisfies readonly SeriesOptionsType['type'][];

/** Where Highcharts' own component keeps its chart-type select. */
const CHART_TYPE_PROPERTY_PATH = ['chartOptions', 'chart', 'type'] as const satisfies readonly [
    keyof HighchartsComponentOptions,
    keyof HighchartsOptions,
    keyof NonNullable<HighchartsOptions['chart']>,
];

let installed = false;

export function installChartTypeOptions(): void {
    if (installed) {
        return;
    }
    installed = true;

    const chartTypeOption = findChartTypeOption(
        Dashboards.HighchartsComponent.defaultOptions.editableOptions ?? []
    );
    if (chartTypeOption) {
        chartTypeOption.selectOptions = DASHBOARD_CHART_TYPES.map((name) => ({ name }));
    } else {
        console.warn(
            `pulse-dashboard: no editable option at '${CHART_TYPE_PROPERTY_PATH.join('.')}' -` +
                " the sidebar keeps Highcharts' own chart types"
        );
    }
}

/** Whether a settings field is the chart type select. */
export function isChartTypeOption(option: EditableOption): boolean {
    return option.propertyPath?.join('.') === CHART_TYPE_PROPERTY_PATH.join('.');
}

/** The chart-type entry, which sits one level down inside the `chartOptions` accordion. */
function findChartTypeOption(options: readonly EditableOption[]): EditableOption | undefined {
    for (const option of options) {
        if (isChartTypeOption(option)) {
            return option;
        }
        const nested: readonly NestedOptions[] = option.nestedOptions ?? [];
        for (const group of nested) {
            const found = findChartTypeOption(group.options);
            if (found) {
                return found;
            }
        }
    }
    return undefined;
}
